/**
 * @description React component for the driver notification selection list.
 * It displays a list of drivers and allows the user to select one or more drivers to notify.
 * Allows to select preference for notification (email or sms)
 * Allows to copy driver link to clipboard
 */
import React from 'react';
import { OrderSharePreviewParams } from '../../redux/api/orders/orders.model';
import { INotifyDriverWithFullSegment } from './TwNotifyDriver';
import NotifySegmentsBody from './TwNotifySegmentsBody';
import { useGetDriversQuery } from '../../redux/api/driver/driverApi';
import Box from '@mui/material/Box';
import useDriverFiltering from './hooks/useDriverFilter';

interface DriverNotificationPreferenceListProps {
  // Core data
  drivers: INotifyDriverWithFullSegment[];
  payload: OrderSharePreviewParams[];
  tripDriversLinkMaps?: Map<string, Map<string, string>> | null;
  canRemove: boolean;

  // Callbacks
  onUpdateDriver: (
    id: string,
    radio: string,
    segmentId: string,
    removed: boolean,
    oldDriverId?: string,
    driverName?: string,
    deletedSegmentIndex?: number,
    textMessage?: string
  ) => void;

  // Configuration
  type: 'assigned' | 'unassigned';
  getDriverLabel: (driver: INotifyDriverWithFullSegment) => string;

  // Optional props
  className?: string;
  style?: React.CSSProperties;
}

const DriverNotificationPreferenceList: React.FC<DriverNotificationPreferenceListProps> = ({
  drivers,
  payload,
  tripDriversLinkMaps,
  onUpdateDriver,
  type,
  getDriverLabel,
  className,
  style,
  canRemove
}) => {
  const { filteredDrivers } = useDriverFiltering({
    drivers,
    payload
  });

  const { data: driversData = { content: [], total: 0 } } = useGetDriversQuery({});

  return (
    <>
      {filteredDrivers.map((driver: INotifyDriverWithFullSegment, index: number) => (
        <Box
          key={`segment-${driver.fullSegment.id}-${index}`}
          className={`notify-driver__container-body ${className || ''}`}
          sx={{
            borderTop: index === 0 ? '1px solid transparent !important' : 'inherit',
            ...style
          }}
        >
          <p className="notify-driver__text-drivers">{getDriverLabel(driver)}</p>
          <NotifySegmentsBody
            driver={driver}
            driverLink={
              type === 'assigned'
                ? tripDriversLinkMaps?.get(driver.fullSegment.id)?.get(driver.driverId) || null
                : null
            }
            driversList={driversData.content}
            handleUpdateDriver={onUpdateDriver}
            allowRemove={canRemove}
          />
        </Box>
      ))}
    </>
  );
};

export default DriverNotificationPreferenceList;
